import { EllipsisHorizontalIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon, Pencil2Icon } from "@radix-ui/react-icons";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useGetInstance } from "src/api/General/instance";
import {
  IntervalData,
  IntervalEnd,
  IntervalStart,
  useCreateInterval,
  useDeleteInterval,
  useGetIntervals,
  useUpdateInterval,
} from "src/api/key-moments/intervals";
import { useGetKeyMoments } from "src/api/key-moments/key-moments";
import InputHeading from "src/components/ui/Headings/InputHeading";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { Loading } from "src/components/ui/loading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/ui/select";
import { TextInput } from "src/components/ui/text-input";
import { TextArea } from "src/components/ui/textArea";

export default function IntervalsSettings() {
  const { instance } = useGetInstance();
  const { data: intervals } = useGetIntervals();

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 items-center border-b border-gray-200/70 px-8 text-sm">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/settings`}>
            <div className="text-gray-800">{instance?.name}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Intervals"}</div>
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-light-background">
        <div className="mx-auto max-w-4xl pt-20">
          <div className="space-y-1 border-gray-200/70 pb-2">
            <div className="text-xl font-medium">Intervals</div>
            <div className="text-sm font-light text-gray-600">
              Intervals enable you to track the time between two key moments
            </div>
          </div>
          {!!intervals ? (
            <>
              {!!intervals?.length ? (
                <div className="mt-8">
                  <div className="mt-6 space-y-6">
                    <div className="flex justify-end">
                      <NewIntervalButton />
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-6 pb-3 shadow-sm">
                      <div className="flex items-center gap-4 border-b border-gray-200/70 pb-3 font-normal text-gray-500">
                        <div className="flex-1 text-sm leading-none">
                          Interval
                        </div>
                        <div className="w-48 text-sm leading-none">Start</div>
                        <div className="w-48 text-sm leading-none">End</div>
                        <div className="w-16" />

                        {/* <div className="w-48 text-sm leading-none">Type</div>
                      <div className="w-48 text-sm leading-none">
                        Created by
                      </div> */}
                      </div>
                      {intervals?.map((interval) => (
                        <IntervalRow key={interval._id} interval={interval} />
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-8">
                  <div className="mt-12 space-y-6">
                    <div className="flex items-center rounded-lg border border-gray-200 bg-white px-6 py-12 text-sm shadow-sm">
                      <div className="ml-20 w-[420px] py-14 px-3">
                        <div className="text-base font-medium">
                          No Intervals
                        </div>
                        <div className="pt-4 font-light leading-relaxed text-gray-700">
                          Intervals represent the time between two key moments.
                          E.g. the time between the kickoff call ending and the
                          data migration ending. They help you track
                          time-to-value between different points of value.
                        </div>
                        <div className="flex space-x-2.5 pt-7">
                          <NewIntervalButton />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <Loading className="mt-20" />
          )}
        </div>
      </div>
    </div>
  );
}

function NewIntervalButton() {
  const [createIntervalOpen, setCreateIntervalOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const createIntervalMutation = useCreateInterval();
  const [intervalStart, setIntervalStart] = useState<IntervalStart | null>(
    null
  );
  const [intervalEnd, setIntervalEnd] = useState<IntervalEnd | null>(null);
  const { data: keyMoments } = useGetKeyMoments();

  useEffect(() => {
    if (createIntervalMutation.isSuccess) {
      setCreateIntervalOpen(false);
      setName("");
      setDescription("");
      setIntervalStart(null);
      setIntervalEnd(null);
    }
  }, [createIntervalMutation.isSuccess]);

  return (
    <Dialog open={createIntervalOpen} onOpenChange={setCreateIntervalOpen}>
      <DialogTrigger asChild>
        <Button variant={"secondary"}>Create Interval</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl p-8">
        <DialogHeader>
          <DialogTitle>New Interval</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col space-y-6 py-4">
          <div className="space-y-1">
            <InputHeading required heading="Name" />
            <TextInput
              className="w-72"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Kickoff end to Migration end"
            />
          </div>
          <div className="flex items-center gap-6">
            <div className="space-y-1">
              <InputHeading required heading="Start" />
              <Select
                value={
                  intervalStart?.type === "keyMoment"
                    ? intervalStart?.keyMoment?._id
                    : undefined
                }
                onValueChange={(value) => {
                  const keyMoment = keyMoments?.find(
                    (keyMoment) => keyMoment._id === value
                  );
                  if (!keyMoment) return;
                  setIntervalStart({
                    type: "keyMoment",
                    keyMoment: { _id: value, name: keyMoment.name },
                    occurrenceRule: "first",
                  });
                }}
              >
                <SelectTrigger className="w-72 h-9">
                  <div className="truncate">
                    {intervalStart ? (
                      intervalStart.type === "keyMoment" ? (
                        intervalStart.keyMoment.name
                      ) : (
                        <span className="text-gray-300">Select start</span>
                      )
                    ) : (
                      <span className="text-gray-300">Select start</span>
                    )}
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {keyMoments?.map((keyMoment) => (
                    <SelectItem key={keyMoment._id} value={keyMoment._id}>
                      {keyMoment.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <InputHeading required heading="End" />
              <Select
                value={
                  intervalEnd?.type === "keyMoment"
                    ? intervalEnd?.keyMoment?._id
                    : undefined
                }
                onValueChange={(value) => {
                  const keyMoment = keyMoments?.find(
                    (keyMoment) => keyMoment._id === value
                  );
                  if (!keyMoment) return;
                  setIntervalEnd({
                    type: "keyMoment",
                    keyMoment: { _id: value, name: keyMoment.name },
                    occurrenceRule: "first",
                  });
                }}
              >
                <SelectTrigger className="w-72 h-9">
                  <div className="truncate">
                    {intervalEnd ? (
                      intervalEnd.type === "keyMoment" ? (
                        intervalEnd.keyMoment.name
                      ) : (
                        <span className="text-gray-300">Select end</span>
                      )
                    ) : (
                      <span className="text-gray-300">Select end</span>
                    )}
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {keyMoments?.map((keyMoment) => (
                    <SelectItem key={keyMoment._id} value={keyMoment._id}>
                      {keyMoment.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <InputHeading heading="Description" />
            <TextArea
              className="w-[600px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Interval between kickoff ending and migration ending"
            />
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <Button
            variant={"secondary"}
            onClick={() => setCreateIntervalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={
              createIntervalMutation.isLoading ||
              !name.trim() ||
              !intervalStart ||
              !intervalEnd
            }
            onClick={() => {
              if (!intervalStart || !intervalEnd) return;
              if (
                intervalStart.type !== "keyMoment" ||
                intervalEnd.type !== "keyMoment"
              )
                return;
              createIntervalMutation.mutate({
                name,
                description,
                start: {
                  type: "keyMoment",
                  keyMomentId: intervalStart.keyMoment._id,
                  occurrenceRule: intervalStart.occurrenceRule,
                },
                end: {
                  type: "keyMoment",
                  keyMomentId: intervalEnd.keyMoment._id,
                  occurrenceRule: intervalEnd.occurrenceRule,
                },
              });
            }}
          >
            {createIntervalMutation.isLoading ? "Creating..." : "Create"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IntervalRow({ interval }: { interval: IntervalData }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [name, setName] = useState(interval.name);
  const updateIntervalMutation = useUpdateInterval();
  const deleteIntervalMutation = useDeleteInterval();
  console.log(interval)

  useEffect(() => {
    if (deleteIntervalMutation.isSuccess) {
      setIsDeleting(false);
    }
  }, [deleteIntervalMutation.isSuccess]);

  return (
    <div className="flex h-10 items-center gap-4 border-b border-gray-100 last:border-0">
      <div className="flex-1 truncate text-sm font-medium">{interval.name}</div>
      <div className="w-48 truncate text-sm">
        {interval.start.type === "keyMoment"
          ? interval.start.keyMoment.name
          : ""}
      </div>
      <div className="w-48 truncate text-sm">
        {interval.end.type === "keyMoment" ? interval.end.keyMoment.name : ""}
      </div>
      <div className="flex w-16 justify-end">
        <DropdownMenu
          open={optionsOpen}
          onOpenChange={(isOpen) => {
            setOptionsOpen(isOpen);
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant={"ghost"}
              className={clsx(
                "h-6 w-6 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
                !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
              )}
            >
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-44" align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem
                // className="py-1 focus:bg-gray-50 focus:text-red-600"
                onClick={(e) => {
                  setIsEditing(true);
                  e.stopPropagation();
                }}
                // className="py-1"
              >
                <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="ml-2 text-sm">Edit</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                // className="py-1 focus:bg-gray-50 focus:text-red-600"
                className="focus:bg-red-50 focus:text-red-600"
                onClick={(e) => {
                  setIsDeleting(true);
                  e.stopPropagation();
                }}
              >
                <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="ml-2 text-sm">Delete</span>
              </DropdownMenuItem>
              {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            <DialogHeader>
              <DialogTitle>Edit Key Moment</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="space-y-1">
                <InputHeading heading="Name" />
                <TextInput
                  className="w-72"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                  placeholder="Resource name..."
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant={"secondary"} onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button
                disabled={
                  name.trim() === interval.name.trim() || name.trim() === ""
                }
                onClick={() => {
                  updateIntervalMutation.mutate({
                    _id: interval._id,
                    name: name,
                  });
                  setIsEditing(false);
                }}
              >
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
          <DialogContent
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            <DialogHeader>
              <DialogTitle>Delete Interval</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4 text-sm">
              Are you sure you want to delete the interval{" "}
              <span className="font-medium">{interval.name}</span>?
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant={"secondary"}
                onClick={() => setIsDeleting(false)}
              >
                Cancel
              </Button>
              <Button
                variant={"destructive"}
                onClick={() => {
                  deleteIntervalMutation.mutate(interval._id);
                }}
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
